import type { MaterialsItem } from "@/lib/materials-api";
import {
  FILE_TYPE_OPTIONS,
  classifyFileType,
  type FileTypeFacet,
} from "@/lib/materials-filters";

/** 产品占位配额：1 GiB（硬编码，后续接账户配额） */
export const MATERIALS_STORAGE_QUOTA_BYTES = 1024 * 1024 * 1024;

/**
 * 近空时首个「已用」色块的最小可见占比（约 0.5% ≈ 细色条）。
 * 仅影响条上展示宽度，不改变真实 used/free 文案数字。
 */
export const STORAGE_USED_MIN_VISIBLE_PERCENT = 0.5;

/** 各类型在存储条上的展示色（非主色紫系，便于对比） */
export const STORAGE_SEGMENT_COLORS: Record<FileTypeFacet, string> = {
  markdown: "bg-teal-500",
  pdf: "bg-rose-500",
  image: "bg-amber-500",
  other: "bg-slate-400",
};

export type MaterialsStorageSegment = {
  id: FileTypeFacet;
  label: string;
  bytes: number;
  /** 该类型文件个数 */
  count: number;
  /** 占配额的比例 0–100 */
  percentOfQuota: number;
  colorClass: string;
};

export type MaterialsStorageStats = {
  /** 当前工作区已加载素材的字节合计 */
  usedBytes: number;
  /** 配额总字节（当前为产品占位 1 GiB） */
  quotaBytes: number;
  /** 未用完的配额字节（不低于 0） */
  freeBytes: number;
  itemCount: number;
  segments: MaterialsStorageSegment[];
};

/** 人类可读容量（B / KB / MB / GB） */
export function formatStorageBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "0 B";
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) {
    const kb = n / 1024;
    return kb < 10 ? `${kb.toFixed(1)} KB` : `${Math.round(kb)} KB`;
  }
  if (n < 1024 * 1024 * 1024) {
    const mb = n / (1024 * 1024);
    return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
  }
  const gb = n / (1024 * 1024 * 1024);
  // 整 GiB 用整数展示（如配额「1 GB」）
  if (Math.abs(gb - Math.round(gb)) < 1e-9) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(2)} GB`;
}

/**
 * 按文件类型汇总已用容量。
 * 段宽相对配额（1 GiB）成正比；剩余为未用配额。
 */
export function aggregateMaterialsStorage(items: MaterialsItem[]): MaterialsStorageStats {
  const bytesByType: Record<FileTypeFacet, number> = {
    markdown: 0,
    pdf: 0,
    image: 0,
    other: 0,
  };
  const countByType: Record<FileTypeFacet, number> = {
    markdown: 0,
    pdf: 0,
    image: 0,
    other: 0,
  };

  for (const item of items) {
    const facet = classifyFileType(item);
    const size = Number.isFinite(item.byte_size) ? Math.max(0, item.byte_size) : 0;
    bytesByType[facet] += size;
    countByType[facet] += 1;
  }

  const usedBytes = Object.values(bytesByType).reduce((a, b) => a + b, 0);
  const quotaBytes = MATERIALS_STORAGE_QUOTA_BYTES;
  const freeBytes = Math.max(0, quotaBytes - usedBytes);

  const segments: MaterialsStorageSegment[] = FILE_TYPE_OPTIONS.map((opt) => {
    const bytes = bytesByType[opt.id];
    return {
      id: opt.id,
      label: opt.label,
      bytes,
      count: countByType[opt.id],
      percentOfQuota: quotaBytes > 0 ? (bytes / quotaBytes) * 100 : 0,
      colorClass: STORAGE_SEGMENT_COLORS[opt.id],
    };
  });

  return {
    usedBytes,
    quotaBytes,
    freeBytes,
    itemCount: items.length,
    segments,
  };
}

export type StorageBarDisplaySegment = {
  id: FileTypeFacet;
  label: string;
  bytes: number;
  /** 条上展示宽度 0–100（可能对首段做了最小可见抬升） */
  displayPercent: number;
  colorClass: string;
};

/**
 * 将真实占比映射为进度条展示宽度。
 * - used = 0：无色块，整条为 muted 轨道（空态）
 * - used > 0 但占比极小：抬升首个有用量分段到最小可见占比，并从 free 扣减，避免「有占用却看不见色块」
 */
export function storageBarDisplaySegments(stats: MaterialsStorageStats): {
  usedSegments: StorageBarDisplaySegment[];
  freePercent: number;
} {
  const used = stats.segments.filter((s) => s.bytes > 0);
  if (used.length === 0 || stats.usedBytes <= 0) {
    return { usedSegments: [], freePercent: 100 };
  }

  let freePercent =
    stats.quotaBytes > 0 ? (stats.freeBytes / stats.quotaBytes) * 100 : 100;

  const usedSegments: StorageBarDisplaySegment[] = used.map((s) => ({
    id: s.id,
    label: s.label,
    bytes: s.bytes,
    displayPercent: s.percentOfQuota,
    colorClass: s.colorClass,
  }));

  const first = usedSegments[0];
  if (first && first.displayPercent < STORAGE_USED_MIN_VISIBLE_PERCENT) {
    const bump = STORAGE_USED_MIN_VISIBLE_PERCENT - first.displayPercent;
    // 仅当仍有可用配额可「借」展示宽度时才抬升，避免夸大接近满额时的占用
    const applied = Math.min(bump, freePercent);
    first.displayPercent += applied;
    freePercent -= applied;
  }

  return { usedSegments, freePercent };
}
